import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Dialog, DialogRoot, Heading, Tab, Tabs } from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';

import { type InsertFilesState, useInsertFilesState } from './state';
import { TabFiles } from './TabFiles';
import { TabUnsplash } from './TabUnsplash';
import { TabURL } from './TabURL';

export interface InsertFilesDialogProps {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
}

export function InsertFilesDialog(props: InsertFilesDialogProps) {
    const [state, setState] = useInsertFilesState();

    const onComplete = (refs: Array<api.ContentRefFile | api.ContentRefURL>) => {
        setState(null);
        state?.onComplete(refs);
    };

    const persistentState = usePersistentValue(state);

    if (!persistentState) {
        return null;
    }

    return (
        <DialogRoot
            isOpen={Boolean(state)}
            onOpenChange={(opened) => {
                if (!opened) {
                    onComplete([]);
                }
            }}
        >
            <InsertFilesDialogContent {...props} {...persistentState} onComplete={onComplete} />
        </DialogRoot>
    );
}

function InsertFilesDialogContent(props: InsertFilesDialogProps & InsertFilesState) {
    const { space, changeRequest, options, onComplete } = props;
    const { title, acceptURL = false } = options;

    const [tab, setTab] = React.useState<'files' | 'url' | 'unsplash'>('files');
    const accept = options.accept ?? 'files';

    return (
        <Dialog detached>
            <div className="flex flex-col gap-3 border-b px-10 pt-6">
                <Heading>{title ?? 'Select files'}</Heading>
                <Tabs>
                    <Tab
                        active={tab === 'files'}
                        onPress={() => setTab('files')}
                        label="Files"
                        leadingIcon={Icon.Files}
                    />
                    {acceptURL !== false ? (
                        <Tab
                            active={tab === 'url'}
                            onPress={() => setTab('url')}
                            label="URL"
                            leadingIcon={Icon.Link}
                        />
                    ) : null}
                    {accept === 'images' && acceptURL !== false ? (
                        <Tab
                            active={tab === 'unsplash'}
                            onPress={() => setTab('unsplash')}
                            leadingIcon={Icon.Unsplash}
                            label="Unsplash"
                        />
                    ) : null}
                </Tabs>
            </div>
            {tab === 'files' ? (
                <TabFiles
                    space={space}
                    changeRequest={changeRequest}
                    accept={accept}
                    onSelect={onComplete}
                />
            ) : null}
            {tab === 'url' ? <TabURL accept={accept} onSelect={onComplete} /> : null}
            {tab === 'unsplash' ? <TabUnsplash onSelect={onComplete} /> : null}
        </Dialog>
    );
}
